import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatarBRL } from '@truefinance/shared';
import type { StatusTransacao, TipoTransacao } from '@truefinance/shared';
import { useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { api, type Transacao } from '../api/cliente.js';
import { Carregando, ErroSeguro, Vazio } from '../componentes/estados.js';
import { LinhaTransacao } from '../componentes/Transacao.js';
import { ModalAcao } from '../componentes/ModalAcao.js';

/**
 * Lista de transações.
 *
 * Entradas e Saídas são esta mesma tela pré-filtrada por `tipoFixo` — o
 * handoff pede as três, mas a diferença é um filtro, e duplicar a tela
 * garantiria que uma correção fosse aplicada em duas e esquecida na terceira.
 */
export function Transacoes({ tipoFixo, titulo }: { tipoFixo?: TipoTransacao; titulo?: string }) {
  const cliente = useQueryClient();
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<StatusTransacao | undefined>(undefined);
  const [pagina, setPagina] = useState(1);
  const [paraExcluir, setParaExcluir] = useState<Transacao | null>(null);

  const filtro = {
    ...(tipoFixo ? { tipo: tipoFixo } : {}),
    ...(status ? { status } : {}),
    ...(busca ? { busca } : {}),
    pagina,
    porPagina: 25,
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transacoes', filtro],
    queryFn: () => api.transacoes(filtro),
  });

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.categorias(),
  });

  const mapaCategorias = new Map(categorias?.categorias.map((c) => [c.id, c.nome]) ?? []);

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-[length:var(--text-tela)] font-semibold text-texto">
          {titulo ?? 'Transações'}
        </h1>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-texto-terciario"
            aria-hidden
          />
          <input
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPagina(1);
            }}
            placeholder="Buscar por descrição ou contraparte"
            className="h-10 w-full rounded-[var(--radius-controle)] border border-borda-input bg-superficie pl-9 pr-3 text-[length:var(--text-corpo)] text-texto placeholder:text-[var(--cor-placeholder)]"
          />
        </label>

        <div className="flex gap-1.5">
          <Chip ativo={status === undefined} aoClicar={() => setStatus(undefined)}>
            Todas
          </Chip>
          <Chip ativo={status === 'confirmada'} aoClicar={() => setStatus('confirmada')}>
            Confirmadas
          </Chip>
          <Chip
            ativo={status === 'pendente_revisao'}
            aoClicar={() => setStatus('pendente_revisao')}
          >
            Pendentes
          </Chip>
        </div>
      </div>

      {isLoading ? (
        <Carregando />
      ) : isError || !data ? (
        <ErroSeguro aoTentarNovamente={() => void refetch()} />
      ) : data.transacoes.length === 0 ? (
        <Vazio
          titulo="Nenhum lançamento encontrado"
          orientacao={
            busca || status
              ? 'Tente ajustar a busca ou os filtros.'
              : 'Mande um gasto ou recebimento no seu WhatsApp que ele aparece aqui.'
          }
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-[var(--radius-card)] border border-borda bg-superficie shadow-[var(--shadow-card)]">
            {data.transacoes.map((t) => (
              <div key={t.id} className="group relative">
                <LinhaTransacao
                  transacao={t}
                  categoria={t.categoriaId ? mapaCategorias.get(t.categoriaId) : null}
                />
                <button
                  type="button"
                  onClick={() => setParaExcluir(t)}
                  aria-label={`Excluir ${t.descricao}`}
                  className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-[var(--radius-controle)] p-1.5 text-texto-terciario hover:bg-saida-bg hover:text-saida group-hover:block"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between text-[length:var(--text-secundario)] text-texto-secundario">
            <span>
              {data.transacoes.length} de <span className="mono">{data.paginacao.total}</span>{' '}
              lançamentos
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagina <= 1}
                onClick={() => setPagina((p) => p - 1)}
                className="rounded-[var(--radius-controle)] border border-borda-input px-2 py-1 disabled:opacity-45"
              >
                ‹
              </button>
              <span className="mono">
                {data.paginacao.pagina}/{data.paginacao.paginas}
              </span>
              <button
                type="button"
                disabled={pagina >= data.paginacao.paginas}
                onClick={() => setPagina((p) => p + 1)}
                className="rounded-[var(--radius-controle)] border border-borda-input px-2 py-1 disabled:opacity-45"
              >
                ›
              </button>
            </div>
          </div>
        </>
      )}

      <ModalAcao
        aberto={paraExcluir !== null}
        titulo="Excluir lançamento"
        variante="destrutiva"
        rotuloConfirmar="Excluir"
        impacto={
          paraExcluir && (
            <>
              <p>
                <strong>{paraExcluir.descricao}</strong>
              </p>
              <p className="mono mt-1">
                {paraExcluir.tipo === 'entrada' ? 'Entrada' : 'Saída'} de{' '}
                {formatarBRL(paraExcluir.valorCentavos)}
              </p>
              <p className="mt-2">
                O lançamento sai do saldo, mas permanece no histórico e na auditoria. Esta ação não
                pode ser desfeita pelo painel.
              </p>
            </>
          )
        }
        aoConfirmar={async (motivo) => {
          if (!paraExcluir) return;
          await api.excluirTransacao(paraExcluir.id, motivo);
          await cliente.invalidateQueries({ queryKey: ['transacoes'] });
          await cliente.invalidateQueries({ queryKey: ['dashboard'] });
        }}
        aoFechar={() => setParaExcluir(null)}
      />
    </div>
  );
}

function Chip({
  ativo,
  aoClicar,
  children,
}: {
  ativo: boolean;
  aoClicar: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      className={`whitespace-nowrap rounded-[var(--radius-chip)] px-3 py-1.5 text-[length:var(--text-secundario)] ${
        ativo
          ? 'bg-[var(--cor-card-escuro)] font-medium text-white'
          : 'border border-borda-input bg-superficie text-texto-secundario hover:border-primaria'
      }`}
    >
      {children}
    </button>
  );
}
